<template>
  <div
    v-if="productPoolMedia && showProductPool"
    :class="['product-pool-cont', classAttributes]"
    :style="styleAttributes"
  >
    <ProsCollection
      v-if="productPoolMigrationEnabled"
      :panel-config="panelConfig"
      :recommendation-config="recommendationConfig"
    />
    <ProductCarousel
      v-else
      :number-of-items="finalNumberOfItems"
      :use-wish-list="true"
      :products="productPoolCollection"
      :link-name="productPoolMedia?.title"
      :link-url="productPoolMedia?.poolLink?.url"
      :disable-slideshow="true"
      :image-width="getImageWidth"
      :productids="!productPoolCollection?.length ? productPoolMedia?.productIds : []"
      :product-description-alignment="productDescriptionAlignment"
      :picture-skeleton="true"
      :show-corner-badge="true"
      :show-review-condensed="isExpNumberOfItemsMcomOn"
      :color-swatch-enhancement="isColorSwatchEnhancement"
      :analytics="{
        isProductpoolMedia: !isEditorialCarousel,
        meta,
        poolName: productPoolMedia?.poolName,
        poolText: productPoolMedia?.poolLink?.text
      }"
      :simplified-thumbnail="meta?.properties?.simplifiedThumbnail"
      :is-simplification-on="isSimplificationOn"
      :simplification-with-v1="simplificationWithV1"
      :open-in-new-tab="openInNewTab"
      :simplification-non-bold-percentage-exp="simplificationNonBoldPercentageExp"
      :simplification-remove-sale-exp="simplificationRemoveSaleExp"
      @viewAllEvent="viewAllClicked"
      @onRightChevronClick="rightChevronClicked"
      @onLeftChevronClick="leftChevronClicked"
    >
      <template
        v-if="productPoolMedia?.poolLink?.name"
        #title
      >
        <div
          v-html="productPoolMedia?.poolLink?.name"
        />
      </template>
    </ProductCarousel>
  </div>
</template>

<script setup>
import {
  onMounted, ref, computed, inject,
} from 'vue';
import { ProsCollection } from '@common-vue/pros-collection';
import { ProductCarousel } from '@common-vue/product-carousel';
import { fireTag } from '@vue-util/tagManager';
import { brand } from 'config';
import { getClassAttributes, getStyleAttributes } from '../../helpers/getAttributes';

const props = defineProps({
  productPoolMedia: {
    type: Object,
    default: () => ({}),
  },
  meta: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  ksMeta: {
    type: Object,
    default: () => ({}),
  },
  numberOfItems: {
    type: Object,
    default: () => ({
      large: 4,
      medium: 5.3,
      small: 2.45,
    }),
  },
  productDescriptionAlignment: {
    type: String,
    required: false,
    validator: (val) => ['center', 'left'].includes(val),
  },
  isEditorialCarousel: {
    type: Boolean,
    default: false,
  },
  isPlpPage: {
    type: String,
    default: null,
  },
});

const experimentIds = inject('experimentIds', '');
const styleAttributes = ref({});
const isSimplificationOn = ref(false);
const simplificationWithV1 = ref(false);
const openInNewTab = ref(false);
const simplificationNonBoldPercentageExp = ref(false);
const simplificationRemoveSaleExp = ref(false);
const isExpNumberOfItemsMcomOn = ref(false);
const isColorSwatchEnhancement = ref(false);

const simplificationExpIds = ['2054-21', '2054-22', '2054-23', '2054-24', '2054-25', '2054-26', '2054-27', '2054-34', '2054-35', '2054-36'];
const idsSimplificationPercent = ['2054-24', '2054-27', '2054-34', '2054-35', '2054-36'];
const idsSimplificationRemSale = ['2054-26', '2054-27'];

const hasSimplificationExp = experimentIds.split(',').map((id) => id.trim()).some((id) => simplificationExpIds.includes(id));

const isPlpPage = props.isPlpPage === 'discovery';
const productPoolMigrationEnabled = isPlpPage
  ? !!props.ksMeta?.properties?.productPoolMigrationEnabled
  : !!props.meta?.properties?.productPoolMigrationEnabled;
const priceSimplificationEnabled = isPlpPage
  ? !!props.ksMeta?.properties?.priceSimplificationEnabled
  : !!props.meta?.properties?.priceSimplificationEnabledSite;
isSimplificationOn.value = hasSimplificationExp && priceSimplificationEnabled;
openInNewTab.value = props.meta?.context?.deviceType === 'DESKTOP' && props.meta?.properties?.carouselOpenInNewTabEnabled;

// checks for new price simplification and mcom number of items flags
if (experimentIds.length) {
  isExpNumberOfItemsMcomOn.value = brand === 'mcom' && experimentIds.includes('2483-22');
  simplificationWithV1.value = experimentIds.includes('2054-22');
  simplificationNonBoldPercentageExp.value = idsSimplificationPercent.some((expIds) => experimentIds.includes(expIds));
  simplificationRemoveSaleExp.value = idsSimplificationRemSale.some((expIds) => experimentIds.includes(expIds));
  isColorSwatchEnhancement.value = experimentIds.includes('2483-21');
}

const classAttributes = getClassAttributes(props.productPoolMedia?.attributes);
const productPoolCollection = props.productPoolMedia?.collection?.map((item) => item?.product) || [];
const prodLen = productPoolCollection?.length;
const showProductPool = computed(() => props.isEditorialCarousel
  || (!isExpNumberOfItemsMcomOn.value && props.productPoolMedia?.displayCount ? prodLen >= props.productPoolMedia?.displayCount : prodLen >= 6));
const getFinalLargeValue = () => {
  if (props.isEditorialCarousel) return 2;
  if (prodLen < 5) return 4;
  return !isExpNumberOfItemsMcomOn.value && props.productPoolMedia?.displayCount ? props.productPoolMedia?.displayCount : 6;
};
const finalNumberOfItems = computed(() => ({ ...props?.numberOfItems, large: getFinalLargeValue() }));
const getImageWidth = computed(() => {
  const windowSizeByDeviceType = {
    desktop: 'large',
    tablet: 'medium',
    phone: 'small',
  };

  const breakpoints = {
    small: 600,
    medium: 1024,
    large: 1440,
  };
  const deviceTypeLocal = isPlpPage
    ? props.ksMeta?.context?.deviceType?.toLowerCase()
    : props.meta?.context?.deviceType?.toLowerCase();
  const windowSize = windowSizeByDeviceType[deviceTypeLocal];

  return props.meta ? breakpoints[windowSize] / props.numberOfItems[windowSize] : 300;
});

const rightTitleIndent = props.productPoolMedia?.attributes?.fullWidth === 'true' ? '20px' : '0';
onMounted(() => {
  if (props.productPoolMedia?.attributes) {
    styleAttributes.value = getStyleAttributes(props.productPoolMedia.attributes);
  }
});

const viewAllClicked = () => {
  fireTag('link', {
    event_name: `hp product pool ${props.productPoolMedia?.title}`,
    link_name: `hp product pool ${props.productPoolMedia?.title}`,
  });
};

const rightChevronClicked = () => {
  fireTag('link', {
    event_name: 'hp product pool_right',
    link_name: 'hp product pool_right',
  });
};

const leftChevronClicked = () => {
  fireTag('link', {
    event_name: 'hp product pool_left',
    link_name: 'hp product pool_left',
  });
};

// ProductPool doesn't include zone name, instead of that it uses the default name 'product_pool_zone'
const recommendationConfig = {
  zone: 'product_pool_zone',
  products: productPoolCollection,
  productIds: prodLen ? [] : props.productPoolMedia?.productIds,
};

const panelConfig = {
  panelType: 'pool',
  title: props.productPoolMedia?.poolLink?.name,
  productDescriptionAlignment: props.productDescriptionAlignment,
  linkName: props.productPoolMedia?.title,
  linkUrl: props.productPoolMedia?.poolLink?.url,
  numberOfItems: finalNumberOfItems.value,
  properties: isPlpPage ? props.ksMeta?.properties : props.meta?.properties,
  analytics: {
    isProductpoolMedia: !props.isEditorialCarousel,
    meta: props.meta,
    poolName: props.productPoolMedia?.poolName,
    poolText: props.productPoolMedia?.poolLink?.text,
  },
};
</script>

<style lang="scss" scoped>
.product-pool-cont :deep(*) {
  .product-thumbnail-container .bottom-overlay-wrapper {
    display: none;
  }
  @include breakpoint(medium down) {
    .title-container {
      padding-right: v-bind(rightTitleIndent);
    }
    .product-description .product-name {
      -webkit-line-clamp: 2;
    }
  }
  @include breakpoint(large) {
    .product-description .product-name {
      -webkit-line-clamp: 1;
    }
  }
}
</style>

<style lang="scss" brand="mcom">
#app-wrapper > div:not(#home-page) {
  .product-pool-cont {
    .product-thumbnail-container {
      .wishlist-heart-large-card, .wishlist-heart-small-card {
        display: none;
      }
    }
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@import "./../../scss/globalAttributes.mcom.scss";

</style>

<style lang="scss" brand="bcom" scoped>
@import "./../../scss/globalAttributes.bcom.scss";

.product-pool-cont :deep(*){
  .badges, .rating, .product-description .product-name {
    display: none;
  }
  .product-thumbnail-container {
    .wishlist-heart-large-card, .wishlist-heart-small-card {
      display: none;
    }
  }

  .product-description {
    .rating {
      margin-top: rem-calc(8px);
    }
    .pricing, .product-brand {
      margin-top: rem-calc(4px);
    }
  }
}
</style>
